import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import type {
  AgentChatState,
  ChatMode,
  IAgentStep,
  IClarificationRequest,
  IPlanStep,
  IToolExecution,
} from './agentChat.types';

const initialState: AgentChatState = {
  mode: 'bubble',
  currentSessionId: null,
  isOpen: false,
  activeSteps: [],
  toolExecutions: [],
  pendingPlan: null,
  pendingClarification: null,
  isStreaming: false,
  isAgentProcessing: false,
  streamingContent: '',
  streamingSessionId: null,
  unreadCount: 0,
  error: null,
  draftMessage: '',
};

export const AgentChatSlice = createSlice({
  name: 'agentChat',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<ChatMode>) => {
      state.mode = action.payload;
      if (action.payload !== 'bubble') {
        state.isOpen = true;
        state.unreadCount = 0;
      }
    },

    openChat: (state) => {
      state.isOpen = true;
      state.unreadCount = 0;
      if (state.mode === 'bubble') state.mode = 'panel';
    },

    closeChat: (state) => {
      state.isOpen = false;
      state.mode = 'bubble';
    },

    setCurrentSession: (state, action: PayloadAction<number | null>) => {
      state.currentSessionId = action.payload;
      state.activeSteps = [];
      state.toolExecutions = [];
      state.pendingPlan = null;
      state.pendingClarification = null;
      state.streamingContent = '';
      state.isStreaming = false;
      state.isAgentProcessing = false;
    },

    appendStreamingChunk: (state, action: PayloadAction<{ sessionId: number; chunk: string }>) => {
      if (state.currentSessionId === action.payload.sessionId) {
        state.isStreaming = true;
        state.streamingContent += action.payload.chunk;
        state.streamingSessionId = action.payload.sessionId;
      }
    },

    finalizeStreaming: (state) => {
      state.isStreaming = false;
      state.isAgentProcessing = false;
      state.streamingContent = '';
      state.streamingSessionId = null;
      state.activeSteps = [];
      // toolExecutions intentionally kept so the right panel view is stable after done
    },

    addAgentStep: (state, action: PayloadAction<{ sessionId: number; step: IAgentStep }>) => {
      if (state.currentSessionId !== action.payload.sessionId) return;
      state.isAgentProcessing = true;
      const step = action.payload.step;
      const existingIdx = state.activeSteps.findIndex((s) => s.type === step.type);
      if (existingIdx >= 0) {
        state.activeSteps[existingIdx] = step;
      } else {
        state.activeSteps.push(step);
      }
    },

    clearAgentSteps: (state) => {
      state.activeSteps = [];
    },

    setToolRunning: (
      state,
      action: PayloadAction<{ sessionId: number; toolName: string; label: string }>,
    ) => {
      if (state.currentSessionId !== action.payload.sessionId) return;
      state.isAgentProcessing = true;
      const existing = state.toolExecutions.find(
        (t) => t.toolName === action.payload.toolName && t.status === 'running',
      );
      if (!existing) {
        state.toolExecutions.push({
          toolName: action.payload.toolName,
          label: action.payload.label,
          status: 'running',
        });
      }
    },

    setToolDone: (
      state,
      action: PayloadAction<{ sessionId: number; toolName: string; result: any }>,
    ) => {
      if (state.currentSessionId !== action.payload.sessionId) return;
      let idx = -1;
      for (let i = state.toolExecutions.length - 1; i >= 0; i--) {
        const t = state.toolExecutions[i] as IToolExecution;
        if (t.toolName === action.payload.toolName && t.status === 'running') {
          idx = i;
          break;
        }
      }
      if (idx >= 0) {
        state.toolExecutions[idx].status = 'done';
        state.toolExecutions[idx].result = action.payload.result;
      }
    },

    setPendingPlan: (
      state,
      action: PayloadAction<{ sessionId: number; plan: IPlanStep[]; messageId: number }>,
    ) => {
      if (state.currentSessionId !== action.payload.sessionId) return;
      state.pendingPlan = {
        steps: action.payload.plan,
        messageId: action.payload.messageId,
      };
    },

    clearPendingPlan: (state) => {
      state.pendingPlan = null;
    },

    setClarification: (
      state,
      action: PayloadAction<{ sessionId: number; clarification: IClarificationRequest }>,
    ) => {
      if (state.currentSessionId !== action.payload.sessionId) return;
      state.pendingClarification = action.payload.clarification;
    },

    clearClarification: (state) => {
      state.pendingClarification = null;
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.isStreaming = false;
    },

    incrementUnread: (state) => {
      if (!state.isOpen) state.unreadCount += 1;
    },

    setDraftMessage: (state, action: PayloadAction<string>) => {
      state.draftMessage = action.payload;
    },

    resetSessionState: (state) => {
      state.activeSteps = [];
      state.toolExecutions = [];
      state.pendingPlan = null;
      state.pendingClarification = null;
      state.streamingContent = '';
      state.isStreaming = false;
      state.isAgentProcessing = false;
      state.error = null;
    },
  },
});

export const {
  setMode,
  openChat,
  closeChat,
  setCurrentSession,
  appendStreamingChunk,
  finalizeStreaming,
  addAgentStep,
  clearAgentSteps,
  setToolRunning,
  setToolDone,
  setPendingPlan,
  clearPendingPlan,
  setClarification,
  clearClarification,
  setError,
  incrementUnread,
  setDraftMessage,
  resetSessionState,
} = AgentChatSlice.actions;
